import { isEqual } from 'es-toolkit';

import { getBoundsFromPoints, mergeBounds, SceneObjectBounds } from '@paragon/three-shared';

import { SelectableItemEntry } from '../library/selection/types';
import { getAppMonitor } from '../monitor';
import { AppDispatch, RootState } from '../store';
import { setActiveCamera } from '../store/actions/cameraActions';
import { sceneHoverCleared } from '../store/actions/sceneHoverActions';
import { setTooltipHidden } from '../store/actions/viewportActions';
import { getDesignCameraZoomCapBoundingBoxSize } from '../store/handlers/designCameraZoomCapHandler';
import { getActiveRoofContainer } from '../store/selectors/activeRoofContainerSelector';
import {
  getAllSelectedItemsBoundsOrNull,
  getBoundsCalculator,
  getInsertionProfileBoundsOrNull,
} from '../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import { isPanningDisabled } from '../store/selectors/isPanningDisabled';
import { isLayoutViewActive } from '../store/selectors/layoutModeSelectors';
import { getActiveSnappingGuideLines } from '../store/selectors/snappingGuideLineSelectors';
import { cameraSlice } from '../store/slices/camera/cameraSlice';
import { calculateDesignCameraRotation, shouldZPositionBePositive } from '../store/slices/camera/flipAndReverse';
import {
  getCurrentCameraType,
  getCurrentCameraTypeAndProperties,
  getCurrentRenderMode,
} from '../store/slices/camera/selectors';
import { CameraProperties, CameraType, serializeCamera } from '../store/slices/camera/types';
import { OrthographicDesignCamera } from './OrthographicDesignCamera';
import { PerspectiveLayoutCamera } from './PerspectiveLayoutCamera';
import {
  getActiveCamera,
  getActiveCameraOrNull,
  getLayoutModeCameraControlsOrNull,
  invalidateFrame,
  pixelDeltaToViewportDelta,
  pixelSpaceToViewportSpace,
  setDevicePixelRatio,
  switchToCameraType,
} from './reactThreeFiberBindings';
import { threeJsDom } from './util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from './util/events/types';
import { LEFT_MOUSE_BUTTON, MIDDLE_MOUSE_BUTTON, MONITORING_LISTENER_ORDER } from './util/events/util';

let getState: () => RootState = () => {
  throw new Error('Get State not initialized.');
};
let dispatch: AppDispatch = () => {
  throw new Error('Dispatch not initialized.');
};

// #region exported interfaces
export function initializeThreeJsStateLayer(getStateFunction: () => RootState, dispatchFunction: AppDispatch) {
  getState = getStateFunction;
  dispatch = dispatchFunction;
}

export function clearSceneHoverFor2DCameraDragIfApplicable(): boolean {
  const state = getState();
  if (getCurrentCameraType(state) !== 'orthographic-layout' || !threeJsDom.mousedDownInScene) return false;

  dispatch(sceneHoverCleared());
  return true;
}

export function registerViewportDomElement(domElement: HTMLDivElement) {
  const resizeObserver = new ResizeObserver(() => updateViewportSize(domElement));
  resizeObserver.observe(domElement);

  threeJsDom.connect(domElement);

  threeJsDom.addGlobalListener('wheel', zoomDesignCameraIfApplicable);
  threeJsDom.addGlobalListener('touchpinch', zoomDesignCameraIfApplicable);
  threeJsDom.addGlobalListener('mousemove', mouseMove);
  threeJsDom.addGlobalListener('mouseup', mouseUp);

  // The scene is a single opaque <canvas> to session replay and DOM analytics, so this
  // is the only usage signal that users are interacting with the drawing at all.
  threeJsDom.addGlobalListener('click', recordCanvasClick, MONITORING_LISTENER_ORDER);
}

function recordCanvasClick() {
  getAppMonitor().recordInteraction('Canvas clicked', { mode: getCurrentRenderMode(getState()) });
}

function updateViewportSize(domElement: HTMLDivElement) {
  getActiveCamera().updateViewportSize(domElement.clientWidth, domElement.clientHeight);
  setDevicePixelRatio(window.devicePixelRatio);
  syncCameraPropertiesToRedux();
}

export function createOrthographicDesignCamera(aspectRatio: number) {
  // The flipped and reversed settings might be set before the camera is created, so we need to consider them when
  // creating the camera.
  const zPositionShouldBePositive = shouldZPositionBePositive(getState().camera.designSettings);
  const initialRotation = calculateDesignCameraRotation(getState().camera.designSettings);
  const activeComponentBoundingBoxSize = getDesignCameraZoomCapBoundingBoxSize(getState());

  return new OrthographicDesignCamera(
    aspectRatio,
    zPositionShouldBePositive,
    initialRotation,
    activeComponentBoundingBoxSize,
  );
}

export function setPropertiesForActiveCamera({ position, rotation, zoom, target }: CameraProperties) {
  const camera = getActiveCamera();

  camera.position.set(position.x, position.y, position.z);
  camera.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  camera.zoom = zoom;

  camera.updateProjectionMatrix();

  const controls = getLayoutModeCameraControlsOrNull();
  if (target && controls) {
    controls.target.set(target.x, target.y, target.z);
  }
}

export function switchToActiveCameraType() {
  const cameraType = getCurrentCameraType(getState());
  switchToCameraType(cameraType);
}

export function changeCameraIfNeeded(type: CameraType) {
  const currentCamera = getActiveCameraOrNull();
  if (currentCamera && currentCamera.type !== type) {
    switchToCameraType(type);
  }
}

function focusActiveCamera(target: SceneObjectBounds) {
  getActiveCamera().focusTo(target);
  syncCameraPropertiesToRedux();
}

/**
 * Focuses the camera on all selected object(s). Will return false if the operation fails for any reason, such as there
 *   being no selected objects, if the selected objects are not a part of the scene, etc.
 */
export function focusCameraOnSelectedObjects(): boolean {
  const camera = getActiveCameraOrNull();
  const bounds = getAllSelectedItemsBoundsOrNull(getState());

  // We need to check that an active camera exists because `focusActiveCamera` assumes that the camera has been created.
  // It's safe to skip focusing if it hasn't been created because we end up calling this method when the camera is first
  //   initialized.
  if (camera && bounds) {
    focusActiveCamera(bounds);
    return true;
  }

  return false;
}

/**
 * Focuses the camera on the profile being inserted. Returns false if a profile is not being inserted.
 */
export function focusCameraOnInsertionProfile(): boolean {
  const state = getState();
  const bounds = getInsertionProfileBoundsOrNull(state);

  // Normally we never have to worry about what camera is active because it is always based on the selected item(s), but
  //   during the first stage of profile insertion (when this method is likely to be called) there will not yet be a
  //   component design to have selected.
  //
  // If you're wondering whether this special check is still needed, try inserting a component design into an empty
  //   project, as the camera will be in layout mode because the project is/was selcted.
  if (!(getActiveCamera() instanceof OrthographicDesignCamera)) {
    dispatch(setActiveCamera('orthographic-design'));
  }

  if (bounds) {
    focusActiveCamera(bounds);
  }

  return Boolean(bounds);
}

/**
 * Focuses the camera on the visible snapping guidelines. Returns false if no snapping guidelines exist or are visible.
 */
export function focusCameraToVisibleSnappingGuideLines(): boolean {
  const guideLines = getActiveSnappingGuideLines(getState());
  if (guideLines.length === 0) return false;

  focusActiveCamera(
    getBoundsFromPoints(
      guideLines.flatMap((line) => [
        { x: line.basePoint.x, y: line.basePoint.y },
        { x: line.endPoint.x, y: line.endPoint.y },
      ]),
    ),
  );

  return true;
}

/**
 * Use this function instead of `focusCameraOnSelectedObject` when you care more about making sure that the camera is
 *   focused on *something* than you do about making sure it's focused on the selected object specifically.
 *
 * If the camera is unable to focus on the selected object for any reason it will instead attempt to focus on the active
 *   component design (or the active roof container / project in layout mode).
 *
 * Returns false if the camera is unable to focus on anything.
 */
export function focusCameraOnSelectedObjectOrFallback(): boolean {
  const successful = focusCameraOnSelectedObjects();
  if (successful) return true;

  const camera = getActiveCameraOrNull();
  const fallbackBounds = getFallbackFocusTargetIfPossible();

  // We need to check that an active camera exists because `focusActiveCamera` assumes that the camera has been created.
  // It's safe to skip focusing if it hasn't been created because we end up calling this method when the camera is first
  //   initialized.
  if (camera && fallbackBounds) {
    focusActiveCamera(fallbackBounds);
    return true;
  }

  return false;
}

/**
 * Similar to `focusCameraOnSelectedObject`, but will never shrink the visible area of the camera. Use this if you need
 *    to ensure that an item being created or edited is visible on screen, but would prefer to not mess with the user's
 *    current view too much.
 *
 * Implemented optimistically, and might not always work well with our current cameras.
 */
export function ensureEntryIsEntirelyVisibleToCamera(entry: SelectableItemEntry): boolean {
  const state = getState();
  const activeCamera = getActiveCamera();

  if (activeCamera instanceof PerspectiveLayoutCamera) {
    console.warn('ensureEntryIsEntirelyVisibleToCamera is not supported for the perspective layout camera.');
    return false;
  }

  const calculateBounds = getBoundsCalculator(state);
  const entryBounds = calculateBounds(entry);
  if (entryBounds == null) return false;

  const currentBounds = activeCamera.getCurrentBounds();
  const updatedBounds = mergeBounds(currentBounds, entryBounds);

  focusActiveCamera(updatedBounds);
  return true;
}

function getFallbackFocusTargetIfPossible() {
  const state = getState();
  const calculateEntryBounds = getBoundsCalculator(state);

  if (isLayoutViewActive(state)) {
    const activeRoofContainer = getActiveRoofContainer(state);
    if (activeRoofContainer) {
      const roofContainerBounds = calculateEntryBounds({ type: 'roofContainer', item: activeRoofContainer });
      if (roofContainerBounds) {
        return roofContainerBounds;
      }
    }

    const context = state.application.openContext;
    if (context?.type === 'project') {
      const project = state.layoutModels[context.projectGuid];
      if (project) {
        const projectBounds = calculateEntryBounds({ type: 'project', item: project });
        if (projectBounds) {
          return projectBounds;
        }
      }
    }
  } else {
    const activeComponentDesign = getActiveComponentDesign(state);
    if (activeComponentDesign) {
      const componentDesignBounds = calculateEntryBounds({ type: 'componentDesign', item: activeComponentDesign });
      if (componentDesignBounds) {
        return componentDesignBounds;
      }
    }
  }
}

// #endregion

// #region sync server related logic

export function syncCameraPropertiesToRedux() {
  const sceneCamera = getActiveCamera();
  const properties = serializeCamera(sceneCamera);

  const [type, stateCamera] = getCurrentCameraTypeAndProperties(getState());
  if (isEqual(stateCamera, properties)) return;

  dispatch(cameraSlice.actions.cameraAltered({ type, properties }));
}

// #endregion

// #region input handling
function mouseMove(event: GlobalThreeJSDomObjectEvent) {
  if (event.metadata.mouseButtons === 0) dispatch(setTooltipHidden(false));

  invalidateFrame();

  if (event.metadata.mouseButtons === LEFT_MOUSE_BUTTON || event.metadata.mouseButtons === MIDDLE_MOUSE_BUTTON) {
    panDesignCameraIfApplicable(event);
  }
}

function mouseUp() {
  dispatch(setTooltipHidden(false));

  // Only the design camera needs this kind of overkill synchronization. Layout cameras perform their own event handling
  //   and will initiate synchronization as needed.
  if (getActiveCamera() instanceof OrthographicDesignCamera) {
    syncCameraPropertiesToRedux();
  }
}

function panDesignCameraIfApplicable(event: GlobalThreeJSDomObjectEvent) {
  if (!threeJsDom.mousedDownInScene || isPanningDisabled(getState()) || event.metadata.previousEventPosition == null) {
    return;
  }

  // ctrl is used to multi-select, so we don't want to pan the camera when it's held down. The layout cameras handle
  //   this themselves, so an explicit check is only needed here.
  if (event.metadata.ctrlKey) return;

  const designCamera = getActiveCamera();
  if (!(designCamera instanceof OrthographicDesignCamera)) {
    return;
  }

  const oldPosition = event.metadata.previousEventPosition.canvasPixelPosition;
  const newPosition = event.eventPosition.canvasPixelPosition;

  const delta = pixelDeltaToViewportDelta(newPosition.x - oldPosition.x, newPosition.y - oldPosition.y);

  designCamera.pan(delta.x * -1, delta.y * -1);
}

function zoomDesignCameraIfApplicable(event: GlobalThreeJSDomObjectEvent) {
  const designCamera = getActiveCamera();
  if (!(designCamera instanceof OrthographicDesignCamera)) {
    return;
  }

  let scrollDelta;
  if (event.metadata.deltaY) {
    scrollDelta = event.metadata.deltaY;
  } else if (event.metadata.pinchDelta) {
    scrollDelta = event.metadata.pinchDelta;
  } else if (event.metadata.deltaY === 0) {
    return;
  } else {
    console.error('Invalid zoom event—could not find required metadata for either scroll or pinch-to-zoom.');
    return;
  }

  const pixelPosition = event.eventPosition.canvasPixelPosition;
  const viewportSpaceCoords = pixelSpaceToViewportSpace(pixelPosition.x, pixelPosition.y);

  designCamera.zoomCamera(viewportSpaceCoords.x, viewportSpaceCoords.y, scrollDelta * 0.001);
  syncCameraPropertiesToRedux();
}

/** This is approximately equal to the length of one arm of the crosshair. */
export const getCameraClickRadius = () => getActiveCamera().getAntiZoomFactor();

// #endregion
